import type { CelebrityResult } from "@/lib/celebrity-results/types";

export const SITE_ORIGIN = "https://restorehair.com";

export type VideoSeoEntry = {
  pagePath: string;
  name: string;
  description: string;
  thumbnailUrl: string;
  contentUrl: string;
  uploadDate: string;
};

const storyUploadDates: Record<string, string> = {
  "RESTORE_DeionSanders_FullStory2021_V1.mp4": "2024-11-05",
  "KevinStreelman%27sStoryRESTOREHair.mp4": "2024-11-21",
};

const absoluteUrl = (value: string) => value.startsWith("http") ? value : `${SITE_ORIGIN}${value}`;
const filename = (value: string) => value.slice(value.lastIndexOf("/") + 1);

export function celebrityVideoSeo(data: CelebrityResult): VideoSeoEntry | undefined {
  const video = data.story?.video;
  const thumbnail = data.story?.image?.src ?? data.posterImage?.src ?? data.heroBannerImage?.src;
  if (!video || !thumbnail) return undefined;
  return {
    pagePath: `/results/${data.slug}/`,
    name: `${data.firstName} ${data.lastName}'s Hair Transplant Story`,
    description: `Watch ${data.firstName} ${data.lastName} share their RESTORE® hair transplant experience and results.`,
    thumbnailUrl: absoluteUrl(thumbnail),
    contentUrl: video,
    uploadDate: storyUploadDates[filename(video)] ?? "2024-11-22",
  };
}

// Google's Video rich result wants uploadDate as a full ISO 8601 date-time
// with a timezone. A bare "2024-12-03" gave two warnings on all 18 video
// pages in URL Inspection on 17 Oct 2026: "Invalid datetime value" and
// "missing a timezone". Midnight America/Chicago (CST, -06:00): every one of
// these videos was published in November or December.
export function videoUploadDateTime(date: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? `${date}T00:00:00-06:00` : date;
}

export function videoObject(entry: VideoSeoEntry) {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: entry.name,
    description: entry.description,
    thumbnailUrl: [entry.thumbnailUrl],
    uploadDate: videoUploadDateTime(entry.uploadDate),
    contentUrl: entry.contentUrl,
    url: `${SITE_ORIGIN}${entry.pagePath}`,
    potentialAction: { "@type": "WatchAction", target: `${SITE_ORIGIN}${entry.pagePath}` },
  };
}

// One watch page per video. Dr. Harris's bio video also plays on
// /what-to-expect-at-your-hair-restoration-consultation/ (same contentUrl);
// his doctor page, where it is the hero, is its watch page, so the
// consultation page has no VideoObject and no sitemap video entry.
// thumbnailUrl is the poster the page's <video> shows.
export const primaryVideoPages: VideoSeoEntry[] = [
  {
    pagePath: "/doctors/dr-james-harris/", name: "Meet Dr. James Harris",
    description: "Meet RESTORE Medical Director Dr. James Harris and learn about his approach to hair restoration.",
    thumbnailUrl: "https://assets.restorehair.com/e51e2aed-3370-4374-9877-b26c4ca09204/dr-harris-scott.webp",
    contentUrl: "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Videos%20with%20Captions/restore_docbio_harris2022withpatients_v2(Original)_1CompressedForWebsite.mp4",
    uploadDate: "2024-12-03",
  },
  {
    pagePath: "/doctors/dr-adam-cloninger/", name: "Meet Dr. Adam Cloninger",
    description: "Meet RESTORE hair restoration physician Dr. Adam Cloninger.",
    thumbnailUrl: `${SITE_ORIGIN}/img/r/a1d9bc3c-cab7-4d38-879e-ac185ce02f52-cloninger-hero.webp`,
    contentUrl: "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Videos%20with%20Captions/Restore_DocBio_Cloninger2022_V1.mp4",
    uploadDate: "2024-12-03",
  },
  {
    pagePath: "/doctors/dr-shelly-lovitt/", name: "Meet Dr. Shelly Lovitt",
    description: "Meet RESTORE Denver hair restoration physician Dr. Shelly Lovitt.",
    thumbnailUrl: `${SITE_ORIGIN}/img/r/0dd0e6a3-5bc2-4350-91ab-05eebf861991-lovitt-hero.webp`,
    contentUrl: "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Videos%20with%20Captions/Restore_DocBio_Lovitt2022_V1.mp4",
    uploadDate: "2024-12-03",
  },
  {
    pagePath: "/non-surgical/", name: "EVERHAIR Non-Surgical Hair Growth Program",
    description: "Learn how the EVERHAIR physician-managed program helps stop shedding and promote hair growth.",
    thumbnailUrl: `${SITE_ORIGIN}/img/r/wp-2025-03-non-surgical-banner-3-1.webp`,
    contentUrl: "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Videos%20with%20Captions/EverHair_2022Commercial_60s_V1.mp4",
    uploadDate: "2024-12-10",
  },
  {
    pagePath: "/hair-sciences-center/", name: "Hair Sciences Center Is Now RESTORE Denver",
    description: "Learn about Hair Sciences Center's partnership with RESTORE and its Denver hair restoration team.",
    thumbnailUrl: `${SITE_ORIGIN}/img/r/wp-2022-05-hair-sciences-center-banner.webp`,
    contentUrl: "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Videos+with+Captions/Restore_BrandMontageStandardFUEWithHarrisBuildup_v1.mp4",
    uploadDate: "2024-12-18",
  },
  {
    pagePath: "/procedure/", name: "F.U.E. Explained: The RESTORE No-Shave FUE Hair Transplant",
    description: "See how the RESTORE® No-Shave FUE™ hair transplant procedure works, step by step.",
    thumbnailUrl: `${SITE_ORIGIN}/img/r/93adff97-2388-4cac-ac5d-9b1110788df6-restore-fue-banner.webp`,
    contentUrl: "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Videos%20with%20Captions/F.U.E.ExplainedRESTOREHairWhatisFUE.mp4",
    uploadDate: "2024-12-03",
  },
  {
    pagePath: "/pricing/", name: "The Cost and Investment to Regrow Your Hair",
    description: "RESTORE® explains hair transplant pricing, financing, and the investment to regrow your hair.",
    thumbnailUrl: "https://assets.restorehair.com/8e6aed0d-5d2b-411e-bcd3-eb637d82ba80/zach-kevin-hero.webp",
    contentUrl: "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Videos%20with%20Captions/CostInvestmenttoRegrowYourHairRESTOREHair.mp4",
    uploadDate: "2024-12-03",
  },
];

export function primaryVideoObject(pagePath: string) {
  const entry = primaryVideoPages.find((video) => video.pagePath === pagePath);
  return entry ? videoObject(entry) : undefined;
}